import { forwardRef, useEffect, useRef, useState } from 'react'
import { AlertCircle, ChevronDown, CornerUpRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  TASK_PRIORITY_LABELS,
  TASK_STATUS_LABELS,
  type TaskPriority,
  type TaskRow,
  type TaskStatus,
} from '@/types/work'
import { SAME_STATUS_NESTED_CAP, type ParentReference } from './kanban-grouping'

const OPEN_STATUSES: ReadonlySet<TaskStatus> = new Set(['pending', 'in_progress', 'on_hold'])

// Plan DR-T2-N: priority badge can't rely on colour alone (WCAG 1.4.1).
// Variant + textual label together cross the bar.
const PRIORITY_BADGE_VARIANT: Record<TaskPriority, 'default' | 'destructive' | 'secondary' | 'outline'> = {
  low: 'outline',
  medium: 'secondary',
  high: 'default',
  urgent: 'destructive',
}

function isOverdue(task: TaskRow): boolean {
  if (!task.due_at) return false
  if (!OPEN_STATUSES.has(task.status)) return false
  return new Date(task.due_at) < new Date()
}

function formatDueDate(due: string): string {
  return new Date(due).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

// Fixed-width slot for the owning team name (R6). The slot keeps a
// constant width across cards so card metadata rows don't shift; the
// name truncates within it. A tooltip with the full name is attached
// only when the text actually overflows the slot (scrollWidth >
// clientWidth) — short names show no tooltip.
function TeamSlot({ name }: { name: string | null }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [overflowing, setOverflowing] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    setOverflowing(el.scrollWidth > el.clientWidth)
  }, [name])

  const slot = (
    <span ref={ref} className="inline-block w-20 shrink-0 truncate align-middle">
      {name ?? '—'}
    </span>
  )

  if (!name || !overflowing) return slot

  return (
    <Tooltip>
      <TooltipTrigger asChild>{slot}</TooltipTrigger>
      <TooltipContent>{name}</TooltipContent>
    </Tooltip>
  )
}

interface TaskKanbanCardProps extends React.HTMLAttributes<HTMLDivElement> {
  task: TaskRow
  // Full list of same-status children rendered as compact mini-rows
  // under the parent. The card slices to SAME_STATUS_NESTED_CAP by
  // default and reveals the rest through the "voir N de plus"
  // disclosure when the user expands (the slice happens at render time
  // so the disclosure has something to reveal).
  nestedChildren?: TaskRow[]
  // When set, this card is a different-status child rendered standalone
  // in its own column. The frame links back to the parent's Show page.
  parentReference?: ParentReference | null
  isDragging?: boolean
  onTaskClick?: (id: number) => void
}

export const TaskKanbanCard = forwardRef<HTMLDivElement, TaskKanbanCardProps>(
  (
    {
      task,
      nestedChildren,
      parentReference,
      isDragging,
      onTaskClick,
      className,
      ...props
    },
    ref,
  ) => {
    const [expanded, setExpanded] = useState(false)
    const overdue = isOverdue(task)
    const priorityVariant = PRIORITY_BADGE_VARIANT[task.priority] ?? 'outline'
    const allNested = nestedChildren ?? []
    const visibleNested = expanded ? allNested : allNested.slice(0, SAME_STATUS_NESTED_CAP)
    const hiddenNested = allNested.length - visibleNested.length

    // Whole-card click + drag (Jira-style): the parent KanbanItem owns
    // the drag activator. When the pointer moves <8px (sensor activation
    // constraint in app/frontend/components/ui/kanban.tsx) the gesture
    // is treated as a click and onTaskClick fires here. Inner anchors
    // and buttons stop propagation so they win when a click lands on
    // them specifically (title's own onClick, child mini-row link,
    // parent banner link, "voir N de plus" disclosure button).
    function handleTitleClick(e: React.MouseEvent<HTMLAnchorElement>) {
      e.stopPropagation()
      // Mirror TaskRow: let modified clicks fall through to native anchor
      // handling so cmd/ctrl/shift/middle-click still opens a new tab.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
      if (!onTaskClick) return
      e.preventDefault()
      onTaskClick(task.id)
    }

    return (
      <Card
        ref={ref}
        className={[
          'group relative overflow-hidden py-0 gap-0 border transition-all duration-200',
          isDragging
            ? 'shadow-xl ring-2 ring-primary/30 scale-[1.02] rotate-1'
            : 'hover:shadow-md hover:border-foreground/15',
          parentReference !== undefined ? 'border-l-4 border-l-primary/40' : '',
          className ?? '',
        ].filter(Boolean).join(' ')}
        {...props}
      >
        {parentReference !== undefined ? (
          <div className="flex items-center gap-1.5 border-b bg-muted/40 px-3 py-1.5 text-[11px] text-muted-foreground">
            <CornerUpRight className="h-3 w-3 shrink-0" aria-hidden />
            <span className="shrink-0">Parent :</span>
            {parentReference ? (
              <a
                href={`/work/tasks/${parentReference.id}`}
                onClick={(e) => e.stopPropagation()}
                className="truncate font-medium text-foreground hover:underline"
                title={parentReference.title}
              >
                {parentReference.title}
              </a>
            ) : (
              <span className="italic">non disponible</span>
            )}
          </div>
        ) : null}

        <CardContent className="px-3 py-3">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <a
                  href={`/work/tasks/${task.id}`}
                  onClick={handleTitleClick}
                  className="flex-1 min-w-0 text-sm font-semibold leading-snug line-clamp-2 hover:underline"
                  title={task.title}
                >
                  {task.title}
                </a>
                <Badge variant={priorityVariant} className="shrink-0">
                  {TASK_PRIORITY_LABELS[task.priority]}
                </Badge>
              </div>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                {task.due_at ? (
                  <span
                    className={overdue ? 'flex items-center gap-1 font-medium text-destructive' : 'flex items-center gap-1'}
                  >
                    {overdue ? <AlertCircle className="h-3 w-3" aria-hidden /> : null}
                    {/* Screen-reader prefix so overdue isn't conveyed by colour alone */}
                    {overdue ? <span className="sr-only">En retard : </span> : null}
                    {formatDueDate(task.due_at)}
                  </span>
                ) : null}
                <span className="truncate">
                  {task.assignee_name ?? <span className="italic">Non assigné</span>}
                </span>
                <span className="flex items-center gap-1">
                  <span className="shrink-0">Équipe :</span>
                  <TeamSlot name={task.team_name} />
                </span>
                <span className="flex min-w-0 items-center gap-1">
                  <span className="shrink-0">Créé par :</span>
                  <span className="truncate">{task.created_by_name ?? '—'}</span>
                </span>
                {task.subtask_count > 0 ? (
                  <span>
                    {task.subtask_count} sous-tâche{task.subtask_count > 1 ? 's' : ''}
                  </span>
                ) : null}
              </div>

              {task.subject ? (
                <p className="truncate text-[11px]">
                  <span className="text-muted-foreground">{task.subject.label}</span>
                  {task.subject.sublabel ? (
                    <span className="ml-1 text-muted-foreground/70">{task.subject.sublabel}</span>
                  ) : null}
                </p>
              ) : null}

              {visibleNested.length > 0 ? (
                <ul className="mt-1 space-y-1 border-t pt-2">
                  {visibleNested.map((child) => (
                    <li key={child.id} className="flex items-center gap-2 text-[11px]">
                      <Badge variant="outline" className="shrink-0">
                        {TASK_STATUS_LABELS[child.status]}
                      </Badge>
                      <a
                        href={`/work/tasks/${child.id}`}
                        onClick={(e) => {
                          // stopPropagation so the card-level click
                          // doesn't also open the parent's sheet.
                          e.stopPropagation()
                          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
                          if (!onTaskClick) return
                          e.preventDefault()
                          onTaskClick(child.id)
                        }}
                        className="truncate hover:underline"
                        title={child.title}
                      >
                        {child.title}
                      </a>
                    </li>
                  ))}
                  {hiddenNested > 0 ? (
                    <li>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setExpanded(true)
                        }}
                        className="flex items-center gap-1 text-[11px] text-primary hover:underline"
                      >
                        <ChevronDown className="h-3 w-3" />
                        voir {hiddenNested} de plus
                      </button>
                    </li>
                  ) : null}
                </ul>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  },
)

TaskKanbanCard.displayName = 'TaskKanbanCard'
